import { css } from '@emotion/react';
import { AIMetadata } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { AIOutcome } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { startCase } from 'lodash-es';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { PhonesUtils } from '@frontend/phone-panels';
import { AiOutcomeComponent } from '@frontend/phone-ui';
import { useScopedAppFlagStore } from '@frontend/scope';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import { Chip, Text, Truncated } from '@frontend/design-system';
import { Dot } from './dot.component';

type HeaderProps = {
  onBack: () => void;
  callFrom?: string;
  callStartTime?: string;
  callStatus?: string;
  aiCallMetadata?: AIMetadata;
  onClose: () => void;
};

export const CallPathMappingHeader = ({
  onBack,
  onClose,
  callFrom,
  callStartTime,
  callStatus,
  aiCallMetadata,
}: HeaderProps) => {
  const { t } = useTranslation('calls');
  const { getCustomizationFlagValue } = useScopedAppFlagStore();

  const hasVoiceAi = getCustomizationFlagValue('truelarkaivoice');
  const outcome = aiCallMetadata?.aiOutcome;

  const handleFeedback = () => {
    window.open(
      'https://feedback.getweave.com/forums/964997-new-weave-app/suggestions/50542602-improve-call-path-map-details',
      '_blank'
    );
  };

  return {
    headerStart: <SurfaceHeader.Icon iconName='caret-left-small' onClick={onBack} />,

    headerTitle: <SurfaceHeader.Title>{t('Call Path')}</SurfaceHeader.Title>,

    headerActions: (
      <>
        <SurfaceHeader.Icon
          iconName='user-voice'
          onClick={handleFeedback}
          hoverLabel={t('Give feedback')}
          hoverLabelPlacement='left'
        />
        <SurfaceHeader.Divider />
        <SurfaceHeader.Icon iconName='x' onClick={onClose} />
      </>
    ),

    headerBody: (
      <>
        <div css={firstRowGridStyles}>
          <Icon name='phone-incoming-small' css={{ color: theme.colors.neutral70 }} />
          <Truncated as={Text} size='medium'>
            {t('Call from {{ name }}', { name: callFrom })}
          </Truncated>
          <Dot size='small' />
          <Truncated as={Text} size='medium'>
            {callStartTime}
          </Truncated>
        </div>
        <div css={secondRowGridStyles}>
          {callStatus && (
            <Chip isResponsive variant={PhonesUtils.getStatusChipColor(callStatus)}>
              {startCase(callStatus)}
            </Chip>
          )}

          {hasVoiceAi && outcome && outcome !== AIOutcome.AI_OUTCOME_UNKNOWN && (
            <>
              <Dot size='small' />
              <AiOutcomeComponent outcome={outcome} />
            </>
          )}
        </div>
      </>
    ),
  };
};

const firstRowGridStyles = css`
  display: grid;
  grid-template-columns: max-content minmax(0, max-content) max-content max-content;
  gap: ${theme.spacing(1)};
  align-items: center;
`;

const secondRowGridStyles = css`
  display: grid;
  grid-template-columns: max-content max-content max-content;
  gap: ${theme.spacing(1)};
  align-items: center;
`;
